import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'

import {
  formatEventDateLabel,
  formatEventTimeLabel,
  formatEventWeekdayTimeLabel,
  isOnlineEventLocation,
  resolveEventDisplayTimeZone,
  toEventAboutLocationLabel,
} from './event-formatters.utils'
import { canViewEventSection, toViewerRoles } from './event-viewer-access.utils'
import { EVENT_DETAIL_ABOUT_COLLAPSED_MAX_HEIGHT_PX, EVENT_DETAIL_SECTION_IDS } from '../constants/event-detail.constants'
import type {
  EventDetailRedirectLink,
  EventCustomTabType,
  EventDetailAppSection,
  EventDetailCustomTab,
  EventDetailLabels,
  EventDetailNavTab,
  EventDetailSectionId,
  EventDetailSummary,
} from '../types/event-detail'
import type {
  EventParticipantRole,
  EventsManagementAttendeeAppSection,
  EventsManagementEvent,
  EventsManagementRedirection,
} from '../types/events-management'

// Route params

export const firstRouteValue = (raw: unknown): string => {
  if (typeof raw === 'string') return raw.trim()
  if (Array.isArray(raw)) return String(raw[0] ?? '').trim()
  return ''
}

export const toEventDetailTabId = (hash: string): string | undefined => {
  const normalized = (hash.startsWith('#') ? hash.slice(1) : hash).trim()
  return normalized || undefined
}

// About

const ENTITY_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
}

const RICH_MEDIA_TAG = /<(img|iframe|video|audio|table|ul|ol|hr|svg)\b/i

export const stripEventHtml = (value: string): string =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/gi, match => ENTITY_MAP[match.toLowerCase()] ?? ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const hasAboutContent = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (stripEventHtml(trimmed)) return true
  return RICH_MEDIA_TAG.test(trimmed)
}

export const isAboutHeightOverflowing = (
  scrollHeight: number,
  collapsedMaxHeightPx = EVENT_DETAIL_ABOUT_COLLAPSED_MAX_HEIGHT_PX
): boolean => scrollHeight > collapsedMaxHeightPx

// Event

const isLikelyEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

// Attendee App

const isSectionId = (value: string): value is EventDetailSectionId => EVENT_DETAIL_SECTION_IDS.includes(value as EventDetailSectionId)

/** Organizer-created section ids are object ids, so they can never collide with a built-in section id. */
const CUSTOM_SECTION_ID_PATTERN = /^[a-f0-9]{24}$/

const toMediaTabType = (mediaType: string, mimeType: string): EventCustomTabType | undefined => {
  if (mediaType === 'pdf' || mediaType === 'image') return mediaType
  if (mimeType === 'application/pdf') return 'pdf'
  return mimeType.startsWith('image/') ? 'image' : undefined
}

const toCustomSection = (section: EventsManagementAttendeeAppSection, id: string, label: string): EventDetailCustomTab | undefined => {
  const source = section.source
  const url = source?.url?.trim() ?? ''
  if (!source || !isExternalHttpsRedirect(url)) return undefined

  const sourceType = source.type?.trim()
  if (sourceType === 'embed' || sourceType === 'external') return { id, kind: 'custom', label, type: sourceType, url }
  if (sourceType !== 'media') return undefined

  const mimeType = source.mimeType?.trim().toLowerCase() ?? ''
  const fileName = source.fileName?.trim() ?? ''
  const type = toMediaTabType(source.mediaType?.trim().toLowerCase() ?? '', mimeType)
  if (!type) return undefined

  return { id, kind: 'custom', label, type, url, ...(mimeType ? { mimeType } : {}), ...(fileName ? { fileName } : {}) }
}

/**
 * The organizer-configured Attendee App sections, in their authored order. Disabled, unlabelled, duplicate and
 * unrenderable sections are dropped; a custom section needs an object id and an https source. A section whose
 * audience excludes the viewer's roles is dropped too, so it is never published.
 */
export const toEventAttendeeAppSections = (
  sections: readonly EventsManagementAttendeeAppSection[] | undefined,
  viewerRoles: readonly EventParticipantRole[] = []
): readonly EventDetailAppSection[] => {
  if (!Array.isArray(sections)) return []

  const seen = new Set<string>()
  const mapped: EventDetailAppSection[] = []
  for (const section of sections) {
    const id = section?.sectionId?.trim() ?? ''
    const label = section?.label?.trim() ?? ''
    if (!id || !label || section.enabled === false || seen.has(id)) continue
    if (!canViewEventSection(section.audience, viewerRoles)) continue

    let mappedSection: EventDetailAppSection | undefined
    if (section.kind === 'built_in' && isSectionId(id)) {
      mappedSection = { id, kind: 'built_in', label }
    } else if (section.kind === 'custom' && CUSTOM_SECTION_ID_PATTERN.test(id) && !isSectionId(id)) {
      mappedSection = toCustomSection(section, id, label)
    }
    if (!mappedSection) continue

    seen.add(id)
    mapped.push(mappedSection)
  }
  return mapped
}

/** The register button follows `cta.action`; a leftover top-level `registrationUrl` is ignored. */
const toRegistrationUrl = (event: EventsManagementEvent): string => {
  if (event.cta?.action?.trim() !== 'register') return ''
  const url = event.cta.url?.trim() ?? ''
  return isExternalHttpsRedirect(url) ? url : ''
}

/** "Oct 8, 2026 · 9:00 AM" for when an online session's join link opens; empty when the API does not say. */
const toJoinAvailableAtLabel = (availableAt: string | null | undefined, timeZone: string | undefined, displayFormat: '12h' | '24h') => {
  if (!availableAt) return ''
  const date = formatEventDateLabel(availableAt, availableAt, timeZone)
  const time = formatEventTimeLabel(availableAt, timeZone, displayFormat)
  return [date, time].filter(Boolean).join(' · ')
}

export const toEventDetailSummary = (event: EventsManagementEvent, labels: EventDetailLabels): EventDetailSummary => {
  const startDateTime = event.startDateTime ?? ''
  const endDateTime = event.endDateTime ?? ''
  const isOnline = isOnlineEventLocation(event.location)
  const displayTimeZone = resolveEventDisplayTimeZone(event.location, event.timezone)
  const displayFormat = event.displayFormat === '24h' ? '24h' : '12h'
  const timeLabel =
    formatEventWeekdayTimeLabel(startDateTime, displayTimeZone, displayFormat) ||
    formatEventTimeLabel(startDateTime, displayTimeZone, displayFormat)
  const { organizer } = event
  const registrationUrl = toRegistrationUrl(event)
  const locationUrl = isOnline ? '' : (event.location?.googleLocation?.trim() ?? '')
  // Online events join through the first session; an ended event cannot be joined even if the API still sends the link.
  const hasEnded = event.eventState === 'past'
  const onlineSession = isOnline && !hasEnded ? event.onlineSessions?.[0] : undefined
  const sessionJoinUrl = onlineSession?.joinUrl?.trim() ?? ''
  const joinUrl = isExternalHttpsRedirect(sessionJoinUrl) ? sessionJoinUrl : ''
  const joinAvailableAtLabel = joinUrl ? '' : toJoinAvailableAtLabel(onlineSession?.availableAt, displayTimeZone, displayFormat)

  return {
    displayFormat,
    timezone: event.timezone,
    isOnline,
    ...(registrationUrl ? { registrationUrl } : {}),
    ...(joinUrl ? { joinUrl } : {}),
    item: {
      id: event.id,
      title: event.name,
      imageUrl: event.coverImage ?? '',
      dateLabel: formatEventDateLabel(startDateTime, endDateTime, displayTimeZone),
      locationLabel: toEventAboutLocationLabel(event.location, labels.online),
      ...(locationUrl && isExternalHttpsRedirect(locationUrl) ? { locationUrl } : {}),
      ...(timeLabel ? { timeLabel } : {}),
      ...(event.eventUrl && isExternalHttpsRedirect(event.eventUrl) ? { eventUrl: event.eventUrl } : {}),
      about: event.description || event.summary || '',
      organizerName: organizer?.name ?? '',
      ...(organizer?.email && isLikelyEmail(organizer.email) ? { organizerEmail: organizer.email } : {}),
      ...(organizer?.phone ? { organizerPhone: organizer.phone } : {}),
      ...(organizer?.website && isExternalHttpsRedirect(organizer.website) ? { organizerWebsite: organizer.website } : {}),
      ...(organizer?.imageUrl ? { organizerImageUrl: organizer.imageUrl } : {}),
      formatKind: isOnline ? 'online' : 'in-person',
      ...(event.eventState === 'live' ? { isLive: true } : {}),
      ...(hasEnded ? { hasEnded: true } : {}),
      ...(joinAvailableAtLabel ? { joinAvailableAtLabel } : {}),
      attendeeAppSections: toEventAttendeeAppSections(event.attendeeApp?.sections, toViewerRoles(event.roles)),
    },
  }
}

// Tabs

/**
 * The tabs are the trusted Attendee App sections alone, already filtered by enablement and audience, in their
 * authored order and with their labels. No sections means no tabs, so the shell shows its empty state.
 * Registration is never checked here.
 */
export const resolveEventDetailVisibleTabs = (
  attendeeAppSections: readonly EventDetailAppSection[] | undefined
): readonly EventDetailNavTab[] => (attendeeAppSections ?? []).map(section => ({ id: section.id, label: section.label }))

export const resolveEventDetailActiveTab = (preferred: string, visibleTabIds: readonly string[]): string | null => {
  if (visibleTabIds.includes(preferred)) return preferred
  return visibleTabIds[0] ?? null
}

/** The link a creative or logo opens: only an `external_url` redirection to an external HTTPS target, else ''. */
export const toExternalRedirectUrl = (redirection: EventsManagementRedirection | undefined): string => {
  if (redirection?.type?.trim() !== 'external_url') return ''
  const url = redirection.url?.trim() ?? ''
  return isExternalHttpsRedirect(url) ? url : ''
}

/** Builds the portal URL of an exhibitor's profile on the current event. */
export type ExhibitorProfileUrl = (exhibitorId: string) => string

/**
 * Where a creative or logo links, resolved once so renderers only bind it: an `external_url` redirection opens the
 * HTTPS page in a new tab, an `exhibitor` redirection opens that exhibitor's profile on this event in place. Anything
 * else links nowhere (`url` is '').
 */
export const toRedirectLink = (
  redirection: EventsManagementRedirection | undefined,
  toExhibitorProfileUrl?: ExhibitorProfileUrl
): EventDetailRedirectLink => {
  const externalUrl = toExternalRedirectUrl(redirection)
  if (externalUrl) return { url: externalUrl, target: '_blank', external: true }
  const exhibitorId = redirection?.type?.trim() === 'exhibitor' ? (redirection.exhibitorId?.trim() ?? '') : ''
  const exhibitorUrl = exhibitorId && toExhibitorProfileUrl ? toExhibitorProfileUrl(exhibitorId) : ''
  return { url: exhibitorUrl, target: '_self', external: false }
}

export const findEventCustomTab = (tabs: readonly EventDetailCustomTab[], tabId: string): EventDetailCustomTab | undefined =>
  tabs.find(tab => tab.id === tabId.trim())

/**
 * The URL an `embed` custom tab frames, or `undefined`. Only HTTPS sources on another origin than the portal are ever
 * framed — the sandbox allows scripts and same-origin, which is only safe for a cross-origin frame.
 */
export const toCustomTabEmbedUrl = (tab: EventDetailCustomTab, portalOrigin: string): string | undefined => {
  if (tab.type !== 'embed' || !isExternalHttpsRedirect(tab.url)) return undefined
  return toUrlOrigin(tab.url) === portalOrigin ? undefined : tab.url
}

/** Origin of an absolute URL; a malformed URL has none, so it never matches the portal. */
const toUrlOrigin = (url: string): string | undefined => {
  try {
    return new URL(url.trim()).origin
  } catch {
    return undefined
  }
}
